import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, BookOpen, CheckCircle2, Clock3, LoaderCircle, Search, ShieldCheck, Sparkles, X } from 'lucide-react';
import { useSiteCopy } from '../content/SiteCopyContext';
import { siteAssetBackground, useSiteAsset } from '../content/SiteAssetsContext';
import { MARD_291 } from '../core/palette';
import type { ContentCenterEntry } from '../types';
import { contentArticleImages, firstContentArticleImage, MarkdownArticle } from './MarkdownArticle';
import { HeroPromotionCarousel } from './HeroPromotionCarousel';
import { useExperience } from '../content/ExperienceContext';
import { SiteFooter } from './SiteFooter';
import { BeadParticleField } from './BeadParticleField';

interface Props {
  kind: 'palette' | 'tutorial';
}

function metaText(entry: ContentCenterEntry) {
  if (entry.kind === 'palette') {
    const count = entry.slug === 'mard-291' ? 291 : Number(entry.colors?.length || entry.metadata.colorCount || 0);
    const series = String(entry.metadata.series || '');
    return `${String(entry.metadata.brand || '品牌色卡')}${series ? ` · ${series}` : ''}${count && !series.includes(String(count)) ? ` · ${count} 色` : ''}`;
  }
  return `${String(entry.metadata.category || '拼豆知识')}${entry.metadata.difficulty ? ` · ${String(entry.metadata.difficulty)}` : ''}${entry.metadata.minutes ? ` · ${String(entry.metadata.minutes)} 分钟` : ''}`;
}

function entryColors(entry: ContentCenterEntry) {
  if (entry.slug === 'mard-291') return MARD_291;
  return entry.colors || [];
}

function entryImage(entry: ContentCenterEntry) {
  if (entry.coverImage) return entry.coverImage;
  return firstContentArticleImage(entry.body, contentArticleImages(entry.metadata));
}

export function ContentCenterPage({ kind }: Props) {
  const copy = useSiteCopy();
  const { locale } = useExperience();
  const heroBackground = useSiteAsset(kind === 'palette' ? 'palette.heroBackground' : 'tutorial.heroBackground');
  const [entries, setEntries] = useState<ContentCenterEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [keyword, setKeyword] = useState('');
  const [active, setActive] = useState<ContentCenterEntry | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetch(`/api/content-center?kind=${kind}${locale ? `&locale=${locale}` : ''}`, { signal: controller.signal, cache: 'no-store' })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || '内容读取失败');
        setEntries(Array.isArray(data.entries) ? data.entries : []);
      })
      .catch((reason) => { if (reason.name !== 'AbortError') setError(reason.message || '内容读取失败'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [kind, locale]);

  const visible = useMemo(() => {
    const query = keyword.trim().toLocaleLowerCase('zh-CN');
    if (!query) return entries;
    return entries.filter((entry) => `${entry.title} ${entry.summary} ${metaText(entry)} ${(entry.colors || []).map((color) => `${color.code} ${color.name}`).join(' ')}`.toLocaleLowerCase('zh-CN').includes(query));
  }, [entries, keyword]);

  return (
    <main id="primary-content" className={`content-center-page ${kind}`}>
      <section className="site-section-hero center-hero" style={siteAssetBackground(heroBackground)}>
        <BeadParticleField />
        <div className="site-section-hero-copy center-hero-copy">
          <span className="center-kicker"><Sparkles size={16} />{copy(kind === 'palette' ? 'center.paletteKicker' : 'center.tutorialKicker')}</span>
          <h1>{copy(kind === 'palette' ? 'center.paletteTitle' : 'center.tutorialTitle')}</h1>
          <p>{copy(kind === 'palette' ? 'center.paletteIntro' : 'center.tutorialIntro')}</p>
          <div className="center-trust"><span><ShieldCheck size={15} />{copy('center.adminMaintained')}</span><span><CheckCircle2 size={15} />{copy('center.publishedTrace')}</span></div>
        </div>
        {heroBackground && <div className="site-hero-media" aria-hidden="true"><img src={heroBackground} alt="" /></div>}
        <HeroPromotionCarousel placement={kind === 'palette' ? 'palettes' : 'tutorials'} />
      </section>

      <section className="center-library">
        <div className="center-library-head page-context-bar">
          <div><strong>{copy(kind === 'palette' ? 'center.paletteSection' : 'center.tutorialSection')}</strong><small>{entries.length} 项内容</small></div>
          <label className="center-search"><Search size={17} /><input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder={copy(kind === 'palette' ? 'center.paletteSearch' : 'center.tutorialSearch')} /></label>
        </div>
        {loading ? <div className="center-state"><LoaderCircle className="spin" size={25} /><span>正在读取内容…</span></div>
          : error ? <div className="center-state error"><strong>暂时无法读取</strong><span>{error}</span></div>
            : visible.length ? <div className="center-card-grid">
              {visible.map((entry) => {
                const colors = entryColors(entry).slice(0, 12);
                const image = entryImage(entry);
                return <article className="center-card" key={entry.id}>
                  <div className="center-card-visual">
                    {image ? <img src={image} alt={`${entry.title}封面`} loading="lazy" /> : entry.kind === 'palette'
                      ? <div className="palette-card-dots">{colors.length ? colors.map((color) => <i key={color.code} style={{ background: color.hex }}><b>{color.code}</b></i>) : <span>色卡资料准备中</span>}</div>
                      : <div className="tutorial-card-topic"><BookOpen size={38} /><span>{String(entry.metadata.category || '拼豆知识')}</span></div>}
                  </div>
                  <div className="center-card-copy">
                    <span>{entry.eyebrow || (kind === 'palette' ? 'PALETTE' : 'GUIDE')}</span>
                    <h2>{entry.title}</h2>
                    <p>{entry.summary}</p>
                    <small>{entry.kind === 'tutorial' && <Clock3 size={14} />}{metaText(entry)}</small>
                    <button type="button" onClick={() => setActive(entry)}>{copy('center.viewDetail')}<ArrowRight size={16} /></button>
                  </div>
                </article>;
              })}
            </div> : <div className="center-state"><strong>{keyword ? '没有匹配内容' : '内容准备中'}</strong><span>{keyword ? '换个关键词试试。' : copy(kind === 'palette' ? 'center.emptyPalette' : 'center.emptyTutorial')}</span></div>}
      </section>

      <SiteFooter />

      {active && <div className="center-detail-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setActive(null); }}>
        <article className="center-detail" role="dialog" aria-modal="true" aria-labelledby="center-detail-title">
          <button className="center-detail-close" type="button" onClick={() => setActive(null)} aria-label="关闭"><X size={20} /></button>
          <span>{active.eyebrow || copy(active.kind === 'palette' ? 'center.paletteKicker' : 'center.tutorialKicker')}</span>
          <h2 id="center-detail-title">{active.title}</h2>
          <p className="center-detail-summary">{active.summary}</p>
          <div className="center-detail-meta">{metaText(active)}</div>
          {active.kind === 'tutorial' && entryImage(active) && <img className="center-detail-cover" src={entryImage(active)} alt={`${active.title}封面`} />}
          {active.kind === 'palette' && <div className="center-palette-grid">{entryColors(active).map((color) => <span key={color.code} title={`${color.code} ${color.hex}`}><i style={{ background: color.hex }} /><b>{color.code}</b>{'name' in color && color.name ? <small>{color.name}</small> : null}</span>)}</div>}
          {active.kind === 'tutorial'
            ? <MarkdownArticle body={active.body} images={contentArticleImages(active.metadata)} className="center-detail-body" />
            : <div className="center-detail-body">{active.body.split(/\n+/).filter(Boolean).map((paragraph, index) => <p key={`${index}-${paragraph.slice(0, 16)}`}>{paragraph}</p>)}</div>}
          {active.kind === 'palette' && <div className="conversion-coming"><ShieldCheck size={17} /><span><strong>支持图纸色卡转换</strong><small>在图纸查看模式点击“转换色卡”，即可按 RGB 近似值切换到已发布品牌色卡；转换仅更新品牌与色号显示，原图纸颜色数据会保留。</small></span></div>}
        </article>
      </div>}
    </main>
  );
}
